import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getAssistantContext, buildSnapshot, getAssistantSelection, buildSelectionSnapshot } from '../utils/assistantContext';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ResponsiveContainer,
  BarChart, Bar,
  PieChart, Pie, Cell,
  LineChart, Line,
  FunnelChart, Funnel, LabelList,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { API_BASE_URL } from '../config/api';
import ToolsContent from './shell/ToolsContent';
import TasksContent from './shell/TasksContent';

/**
 * AssistantLauncher — single consolidated component for the AI assistant.
 *
 * Architecture:
 *   - ONE bar at bottom-left, always rendered identically (same DOM,
 *     same dimensions, same position) whether the assistant is active
 *     or not. Seamless transition guaranteed.
 *   - When active, additional floating elements appear ABOVE the bar:
 *     the messages area (resizable) and a close button. The bar itself
 *     doesn't change.
 *   - No separate "panel" component or header. Everything lives here.
 *
 * Bar layout (constant):
 *
 *     ┌─────────────────────────────────────────────┐
 *     │ ☰  [ Ask the assistant… ]            +   ↑  │
 *     └─────────────────────────────────────────────┘
 *
 *   ☰  history toggle
 *   +  new conversation
 *   ↑  send (disabled when empty)
 *
 * Active-state extras (around the bar):
 *   - Messages stream above (resizable by dragging top edge, right edge,
 *     or top-right corner)
 *   - × close button floating at top-right of the messages area
 *   - History dropdown overlay when ☰ is clicked
 *
 * Resizing:
 *   - Persistent dimensions (localStorage). Width applies to bar AND
 *     messages container. Height applies to messages container only.
 *   - Three drag zones: top edge (height), right edge (width), top-right
 *     corner (both). Cursor changes on hover.
 *
 * Visibility:
 *   - Renders only when authenticated and on internal routes.
 *   - The launcher checks `authToken` (NOT `token` — the rest of the app
 *     uses `authToken`).
 */

// ─── Constants ─────────────────────────────────────────────────────────────

const STARTER_PROMPTS = [
  "How many active searches do I have? List with candidate counts.",
  "Show me candidates in New York with quant in their title.",
  "What's the status of my most recent search?",
  "Which of my searches has the most candidates rated 4 or higher?",
];

const HIDDEN_PATHS = [
  '/login', '/client/login', '/privacy', '/terms', '/sms-consent', '/messaging',
  '/sms', '/forgot-password', '/reset-password', '/client/forgot-password',
  '/client/reset-password',
];

const TOOL_LABELS = {
  search_candidates: 'Searching candidates',
  get_candidate: 'Loading candidate profile',
  search_pipelines: 'Looking up searches',
  get_pipeline_status: 'Loading status report',
  get_pipeline_candidates: 'Loading pipeline candidates',
  list_my_searches: 'Looking up your searches',
  render_chart: 'Rendering chart',
  add_note: 'Adding note',
  tag_candidate: 'Applying tag',
  remove_tag: 'Removing tag',
  move_candidate_stage: 'Moving candidate',
  add_candidate_to_pipeline: 'Adding to pipeline',
  update_candidate_rating: 'Updating rating',
};

// Tool names that mutate state. Used to gate the post-write window
// event (we don't want to fire 'banyak:assistant_write' after a read
// tool returns, only after writes that actually changed something).
// Keep in sync with the write-tool definitions in
// server/src/services/assistantWriteTools.js.
const WRITE_TOOL_NAMES = new Set([
  'add_note',
  'tag_candidate',
  'remove_tag',
  'move_candidate_stage',
  'add_candidate_to_pipeline',
  'update_candidate_rating',
]);

const MIN_WIDTH = 360;
const MIN_HEIGHT = 280;
const DEFAULT_WIDTH = 448;
const DEFAULT_HEIGHT = 600;

// ─── Helpers ───────────────────────────────────────────────────────────────

function getAuthHeaders() {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function readUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function shouldHide(pathname) {
  if (!pathname) return true;
  if (HIDDEN_PATHS.some(p => pathname === p || pathname.startsWith(p + '/'))) return true;
  if (pathname.startsWith('/client/')) return true;
  return false;
}

function clamp(v, lo, hi) {
  return Math.min(Math.max(v, lo), hi);
}

// ─── Main component ────────────────────────────────────────────────────────

const AssistantLauncher = () => {
  // Visibility (logged-in + internal route + non-client)
  const [visible, setVisible] = useState(false);
  const [pathname, setPathname] = useState(window.location.pathname);

  // Active-state (chat panel open above the bar)
  const [open, setOpen] = useState(false);

  // Persistent dimensions
  const [width, setWidth] = useState(() => {
    const v = parseInt(localStorage.getItem('assistant_width'), 10);
    return v >= MIN_WIDTH ? v : DEFAULT_WIDTH;
  });
  const [height, setHeight] = useState(() => {
    const v = parseInt(localStorage.getItem('assistant_height'), 10);
    return v >= MIN_HEIGHT ? v : DEFAULT_HEIGHT;
  });
  useEffect(() => { try { localStorage.setItem('assistant_width', String(width)); } catch {} }, [width]);
  useEffect(() => { try { localStorage.setItem('assistant_height', String(height)); } catch {} }, [height]);

  // Chat state
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [activeOverlay, setActiveOverlay] = useState(null); // null | 'history' | 'tools' | 'tasks'
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState(null);
  // Pending send: user typed in bar while closed and hit Enter; we open
  // the panel then auto-send once the conversation is created.
  const [pendingSend, setPendingSend] = useState(false);

  // Inline mode: when the assistant sidebar is open on its chat view, it
  // exposes a slot element (#assistant-chat-slot). The launcher then
  // renders its chat surface INTO that slot (via portal) instead of the
  // floating box, and suppresses the floating shell. This is how the chat
  // lives inside the sidebar without duplicating the 1,800-line engine.
  const [inlineSlot, setInlineSlot] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useEffect(() => {
    const find = () => setInlineSlot(document.getElementById('assistant-chat-slot'));
    find();
    const onSlot = () => find();
    const onSidebar = (e) => { setSidebarOpen(!!e?.detail?.open); find(); };
    window.addEventListener('banyak:assistant_chat_slot', onSlot);
    window.addEventListener('banyak:assistant_sidebar_state', onSidebar);
    const t = setInterval(find, 400);
    const stop = setTimeout(() => clearInterval(t), 4000);
    return () => {
      window.removeEventListener('banyak:assistant_chat_slot', onSlot);
      window.removeEventListener('banyak:assistant_sidebar_state', onSidebar);
      clearInterval(t); clearTimeout(stop);
    };
  }, []);
  const inline = !!inlineSlot;
  useEffect(() => {
    // In inline mode the sidebar owns visibility; keep the engine "open"
    // so the send/stream/pending logic (which gates on `open`) runs.
    if (inline) setOpen(true);
  }, [inline]);

  const inputRef = useRef(null);
  const messageEndRef = useRef(null);
  // Tracks the AbortController for any in-flight streaming fetch so we
  // can cancel it the moment the user closes the panel. Without this,
  // pressing the close X would leave the fetch running invisibly in the
  // background (and the server would keep billing Anthropic tokens for
  // a response no one will see).
  const abortRef = useRef(null);

  // Close the panel and cancel any active stream. Used by the floating
  // close X, Escape key, and Cmd/Ctrl+K toggle. Setting open=false
  // alone does NOT stop the fetch; we have to abort the signal so the
  // browser tears down the HTTP connection, which fires req.on('close')
  // server-side and propagates into the Anthropic SDK call.
  //
  // Also resets activeOverlay so the pill's history icon returns to the
  // hamburger state on next open. Without this, closing the panel while
  // an overlay was showing would leave the pill's icon stuck as X even
  // though there's nothing to close anymore.
  const closePanel = () => {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch {}
      abortRef.current = null;
    }
    setActiveOverlay(null);
    setOpen(false);
  };

  // ── pathname tracking (we live outside <Routes>) ───────────────────────
  useEffect(() => {
    const handlePathChange = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', handlePathChange);
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function (...args) {
      origPush.apply(this, args);
      handlePathChange();
    };
    window.history.replaceState = function (...args) {
      origReplace.apply(this, args);
      handlePathChange();
    };
    return () => {
      window.removeEventListener('popstate', handlePathChange);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, []);

  // ── visibility check ───────────────────────────────────────────────────
  useEffect(() => {
    const check = () => {
      const token = localStorage.getItem('authToken');
      if (!token) { setVisible(false); return; }
      const user = readUser();
      if (user?.user_type === 'client') { setVisible(false); return; }
      if (shouldHide(pathname)) { setVisible(false); return; }
      setVisible(true);
    };
    check();
    const iv = setInterval(check, 1500);
    window.addEventListener('storage', check);
    return () => {
      clearInterval(iv);
      window.removeEventListener('storage', check);
    };
  }, [pathname]);

  // ── Cmd/Ctrl+K toggle ──────────────────────────────────────────────────
  useEffect(() => {
    if (!visible) return;
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (open) closePanel(); else setOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line
  }, [visible, open]);

  // ── Escape closes ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') closePanel(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line
  }, [open]);

  // ── Bridge from the shell's bottom pill ────────────────────────────────
  // The shell's BottomPill accepts input and dispatches this event on
  // Enter. We open the panel, seed the draft with the pill's text, and
  // let the existing pendingSend chain (start conversation → auto-send)
  // fire. This is what makes the new pill act as the assistant entry
  // point while the launcher owns the actual conversation UI.
  useEffect(() => {
    if (!visible) return;
    const handler = (e) => {
      const text = e?.detail?.text?.trim();
      if (!text) return;
      setDraft(text);
      setOpen(true);
      setPendingSend(true);
    };
    window.addEventListener('banyak:assistant_open_with_text', handler);
    return () => window.removeEventListener('banyak:assistant_open_with_text', handler);
  }, [visible]);

  // Open the panel without sending anything — dispatched by the pill on
  // the user's first keystroke. The user sees the conversation surface
  // materialize as they type; sending happens on Enter via the "with
  // text" event above.
  useEffect(() => {
    if (!visible) return;
    const handler = () => setOpen(true);
    window.addEventListener('banyak:assistant_open', handler);
    return () => window.removeEventListener('banyak:assistant_open', handler);
  }, [visible]);

  // Broadcast the panel's open/closed state so the shell can hide its
  // bottom pill while the launcher is active (avoids two competing input
  // surfaces on-screen). Fires once per state transition.
  //
  // Payload includes currentTitle and activeOverlay ('history' | 'tools'
  // | 'tasks' | null) so the pill knows which overlay is active and can
  // render its icons accordingly (X for the active overlay's button,
  // regular icons for the others).
  useEffect(() => {
    const currentConv = conversations.find(c => c.id === activeConvId);
    const currentTitle = currentConv?.title || null;
    window.dispatchEvent(
      new CustomEvent('banyak:assistant_state', {
        detail: { open, width, activeOverlay, currentTitle },
      })
    );
  }, [open, width, activeOverlay, activeConvId, conversations]);

  // Track the pill's current height (dispatched from BottomPill via
  // banyak:pill_height_change). We use this to position the launcher's
  // floating column ~10px above the pill regardless of how tall the pill
  // has grown.
  const [pillHeight, setPillHeight] = useState(44);
  useEffect(() => {
    if (!visible) return;
    const handler = (e) => {
      const h = e?.detail?.height;
      if (typeof h === 'number' && h > 0) setPillHeight(h);
    };
    window.addEventListener('banyak:pill_height_change', handler);
    return () => window.removeEventListener('banyak:pill_height_change', handler);
  }, [visible]);

  // Toggle overlays from the pill. Each button in the pill has its own
  // event; the launcher opens the panel if closed and toggles the
  // relevant overlay. If the requested overlay is already active,
  // toggling closes it. This is what makes the buttons behave like
  // tabs — clicking a different one switches, clicking the same one
  // closes back to the messages view.
  useEffect(() => {
    if (!visible) return;
    const historyHandler = () => {
      if (!open) setOpen(true);
      setActiveOverlay((cur) => (cur === 'history' ? null : 'history'));
    };
    const toolsHandler = () => {
      if (!open) setOpen(true);
      setActiveOverlay((cur) => (cur === 'tools' ? null : 'tools'));
    };
    const tasksHandler = () => {
      if (!open) setOpen(true);
      setActiveOverlay((cur) => (cur === 'tasks' ? null : 'tasks'));
    };
    const newChatHandler = () => {
      if (!open) setOpen(true);
      setActiveOverlay(null);
      startNewConversation();
    };
    window.addEventListener('banyak:assistant_toggle_history', historyHandler);
    window.addEventListener('banyak:assistant_toggle_tools', toolsHandler);
    window.addEventListener('banyak:assistant_toggle_tasks', tasksHandler);
    window.addEventListener('banyak:assistant_new_chat', newChatHandler);
    return () => {
      window.removeEventListener('banyak:assistant_toggle_history', historyHandler);
      window.removeEventListener('banyak:assistant_toggle_tools', toolsHandler);
      window.removeEventListener('banyak:assistant_toggle_tasks', tasksHandler);
      window.removeEventListener('banyak:assistant_new_chat', newChatHandler);
    };
  }, [visible, open]);

  // ── chat lifecycle ─────────────────────────────────────────────────────
  // Load conversations when panel opens.
  //
  // Branches based on how the panel was opened:
  //   1. activeOverlay set — user clicked the pill's Chats/Tools/Tasks
  //      the conversation list for the overlay only; don't touch the
  //      underlying conversation state.
  //   2. activeConvId already set — user closed the panel mid-chat and
  //      reopened. Keep the existing conversation. The pendingSend
  //      auto-send effect will fire with the existing activeConvId so
  //      new messages append to the same thread. Don't start a new one.
  //   3. pendingSend true (and no active conversation) — user typed
  //      fresh from the pill with no prior conversation. Start a new one.
  //   4. Otherwise — user opened via first-keystroke or event with no
  //      prior conversation. Load the most recent, or start a new one
  //      if none exist.
  useEffect(() => {
    if (!open) return;
    const openedForOverlay = activeOverlay !== null;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_BASE_URL}/api/assistant/conversations`, { headers: getAuthHeaders() });
        if (cancelled) return;
        const list = r.data?.conversations || [];
        setConversations(list);
        if (openedForOverlay) return;
        if (activeConvId) {
          // Existing conversation — pendingSend (if set) will fire
          // against it via the auto-send effect. Nothing to do here.
          return;
        }
        if (pendingSend) {
          startNewConversation();
        } else if (list.length > 0) {
          loadConversation(list[0].id);
        } else {
          startNewConversation();
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to load conversations:', e);
          setError('Could not load conversations.');
        }
      }
    })();
    refreshUsage();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [open]);

  // Auto-send when pendingSend is set + conversation is ready
  useEffect(() => {
    if (pendingSend && open && activeConvId && !sending) {
      setPendingSend(false);
      setTimeout(() => sendMessage(), 0);
    }
    // eslint-disable-next-line
  }, [pendingSend, open, activeConvId, sending]);

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, sending]);

  // When the panel opens (goes inline) or a conversation loads, jump to the
  // bottom immediately (no smooth animation) so the chat starts on the most
  // recent messages instead of scrolled to the top.
  useEffect(() => {
    if (inline || open) {
      // Double rAF: wait for the portal/messages to paint before scrolling.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        messageEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
      }));
    }
  }, [inline, open, activeConvId]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [activeConvId, open]);

  // ── API ────────────────────────────────────────────────────────────────
  const refreshUsage = async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}/api/assistant/usage`, { headers: getAuthHeaders() });
      setUsage(r.data);
    } catch {}
  };

  const refreshConversations = async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}/api/assistant/conversations`, { headers: getAuthHeaders() });
      setConversations(r.data?.conversations || []);
    } catch {}
  };

  const startNewConversation = async () => {
    try {
      setError(null);
      const r = await axios.post(`${API_BASE_URL}/api/assistant/conversations`, {}, { headers: getAuthHeaders() });
      setActiveConvId(r.data?.id);
      setMessages([]);
      setActiveOverlay(null);
      refreshConversations();
    } catch (e) {
      console.error('startNewConversation failed:', e);
      setError('Could not start a new conversation.');
    }
  };

  const loadConversation = async (id) => {
    if (!id) return;
    try {
      setError(null);
      const r = await axios.get(`${API_BASE_URL}/api/assistant/conversations/${id}`, { headers: getAuthHeaders() });
      setActiveConvId(id);
      setMessages(r.data?.messages || []);
      setActiveOverlay(null);
    } catch (e) {
      console.error('loadConversation failed:', e);
      setError('Could not load that conversation.');
    }
  };

  const archiveConversation = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Archive this conversation?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/assistant/conversations/${id}`, { headers: getAuthHeaders() });
      if (id === activeConvId) startNewConversation();
      else refreshConversations();
    } catch (e) {
      console.error('archiveConversation failed:', e);
    }
  };

  // In-flight assistant message being built up from SSE events.
  // Separate from `messages` (which is the canonical persisted log) so
  // the streaming reply renders progressively without interfering with
  // the saved history. Cleared once the stream completes and we refetch
  // the canonical messages from the server.
  const [streamingMsg, setStreamingMsg] = useState(null);

  const sendMessage = async () => {
    const text = draft.trim();
    if (!text || sending || !activeConvId) return;
    setSending(true);
    setError(null);
    setDraft('');

    // Optimistic insertion of the user's message — appears immediately.
    setMessages(prev => [...prev, {
      id: `optimistic-${Date.now()}`,
      role: 'user',
      content: [{ type: 'text', text }],
      created_at: new Date().toISOString(),
    }]);

    // Initialize the in-flight assistant message. Content array grows as
    // events arrive: tool_use blocks get appended on tool_use_start,
    // text accumulates into the last text block on text_delta.
    setStreamingMsg({
      id: `streaming-${Date.now()}`,
      role: 'assistant',
      content: [],
      created_at: new Date().toISOString(),
    });

    try {
      // Create an AbortController for this fetch and stash it so
      // closePanel() can tear it down if the user closes mid-stream.
      const controller = new AbortController();
      abortRef.current = controller;

      const response = await fetch(`${API_BASE_URL}/api/assistant/messages/stream`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversation_id: activeConvId,
          content: text,
          // Lock-on: include a bounded snapshot of the page the user is
          // viewing so the assistant can analyze what's on screen. Null
          // when unlocked or on an unsupported page.
          page_context: (() => {
            try {
              const ctx = getAssistantContext();
              return ctx ? buildSnapshot(ctx) : null;
            } catch { return null; }
          })(),
          selection_context: (() => {
            try {
              const sel = getAssistantSelection();
              return sel ? buildSelectionSnapshot(sel) : null;
            } catch { return null; }
          })(),
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        // Cost cap, auth failure, etc — pre-stream JSON error response.
        const errBody = await response.json().catch(() => ({}));
        const apiErr = errBody.error;
        const code = errBody.code;
        if (response.status === 429 && code?.startsWith('COST_CAP_')) {
          setError(apiErr || 'Daily/monthly budget reached.');
        } else if (response.status === 401) {
          setError('Your session expired. Please log in again.');
        } else {
          setError(apiErr || `Request failed (${response.status}).`);
        }
        setStreamingMsg(null);
        return;
      }

      // ── Consume SSE stream ──────────────────────────────────────────
      // SSE events are separated by \n\n. Each event has lines like
      // "event: TYPE" and "data: JSON". Parse them out and dispatch.
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      // Helper: append a text delta to the last text block, or start a
      // new text block if the current trailing block is something else
      // (e.g. a tool_use). Uses functional setState so concurrent
      // updates don't lose deltas.
      const appendText = (delta) => {
        setStreamingMsg(prev => {
          if (!prev) return prev;
          const content = [...prev.content];
          const last = content[content.length - 1];
          if (last && last.type === 'text') {
            content[content.length - 1] = { ...last, text: (last.text || '') + delta };
          } else {
            content.push({ type: 'text', text: delta });
          }
          return { ...prev, content };
        });
      };

      const addToolUse = (id, name, input, status = 'pending', preview = null) => {
        setStreamingMsg(prev => {
          if (!prev) return prev;
          // If a tool_use with this id already exists (e.g. confirmation
          // event after a placeholder), update it instead of duplicating.
          const existingIdx = prev.content.findIndex(b => b.type === 'tool_use' && b.id === id);
          if (existingIdx >= 0) {
            const content = [...prev.content];
            content[existingIdx] = { ...content[existingIdx], name, input, status, preview };
            return { ...prev, content };
          }
          return {
            ...prev,
            content: [...prev.content, { type: 'tool_use', id, name, input, status, preview }],
          };
        });
      };

      const updateToolStatus = (id, status) => {
        setStreamingMsg(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            content: prev.content.map(b =>
              b.type === 'tool_use' && b.id === id ? { ...b, status } : b
            ),
          };
        });
      };

      let streamDone = false;
      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Extract complete events (separated by \n\n). The last entry in
        // the split is potentially incomplete — keep it in buffer.
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';

        for (const eventStr of parts) {
          if (!eventStr.trim() || eventStr.startsWith(':')) continue; // comment / keepalive
          let eventType = 'message';
          let dataLines = [];
          for (const line of eventStr.split('\n')) {
            if (line.startsWith('event: ')) eventType = line.slice(7).trim();
            else if (line.startsWith('event:')) eventType = line.slice(6).trim();
            else if (line.startsWith('data: ')) dataLines.push(line.slice(6));
            else if (line.startsWith('data:')) dataLines.push(line.slice(5));
          }
          if (dataLines.length === 0) continue;
          let payload;
          try { payload = JSON.parse(dataLines.join('\n')); } catch { continue; }

          switch (eventType) {
            case 'tool_use_start':
              addToolUse(payload.id, payload.name, payload.input);
              break;
            case 'tool_executing':
              updateToolStatus(payload.id, 'executing');
              break;
            case 'tool_result':
              updateToolStatus(
                payload.id,
                payload.is_error ? 'error' :
                payload.cancelled ? 'cancelled' :
                payload.timed_out ? 'timeout' :
                'complete'
              );
              // Belt-and-suspenders auto-refresh: on every SUCCESSFUL
              // write-tool result, fire a window CustomEvent so any UI
              // not subscribed to pipeline SSE (like NotesPanel, the
              // candidate header tag pills) can refresh itself.
              //
              // The server emits this event with input + result so we
              // know which entity changed. Listeners filter by tool +
              // candidate_id / card_id to decide whether to refresh.
              if (
                payload.name && payload.input &&
                !payload.is_error && !payload.cancelled && !payload.timed_out &&
                WRITE_TOOL_NAMES.has(payload.name)
              ) {
                try {
                  window.dispatchEvent(new CustomEvent('banyak:assistant_write', {
                    detail: {
                      tool: payload.name,
                      input: payload.input,
                      result: payload.result,
                    },
                  }));
                } catch {}
              }
              break;
            case 'confirmation_required':
              // Write tool waiting for user approval. Mark the
              // tool_use block with status='awaiting_confirmation' and
              // attach the preview so the confirmation card has
              // everything it needs to render.
              addToolUse(payload.id, payload.name, payload.input, 'awaiting_confirmation', payload.preview);
              break;
            case 'text_delta':
              if (payload.delta) appendText(payload.delta);
              break;
            case 'error':
              setError(payload.message || 'Assistant error');
              break;
            case 'done':
              streamDone = true;
              break;
            case 'aborted':
              streamDone = true;
              break;
            default:
              break;
          }
        }
      }

      // ── Finalize: refetch canonical messages from the server ──────────
      // The streaming version is a UI optimization; the source of truth
      // lives in assistant_messages. Refetching guarantees we end up
      // with the exact persisted state (including any partial saves
      // from a mid-stream abort).
      try {
        const r = await axios.get(`${API_BASE_URL}/api/assistant/conversations/${activeConvId}`, { headers: getAuthHeaders() });
        setMessages(r.data?.messages || []);
      } catch {}
      refreshUsage();
      refreshConversations();
    } catch (e) {
      if (e?.name === 'AbortError') {
        // User cancelled the stream (closed the panel mid-reply). Not
        // a real error — the server has already persisted whatever was
        // generated before the abort, tagged with an ABORTED marker.
        // Refetch the conversation so the partial assistant message
        // shows up next time the user reopens the panel or this
        // conversation.
        try {
          const r = await axios.get(`${API_BASE_URL}/api/assistant/conversations/${activeConvId}`, { headers: getAuthHeaders() });
          setMessages(r.data?.messages || []);
        } catch {}
      } else {
        console.error('streaming send failed:', e);
        setError(e?.message || 'Could not send message.');
      }
    } finally {
      abortRef.current = null;
      setStreamingMsg(null);
      setSending(false);
    }
  };

  // ── confirmation handler (Ship 4: write tools) ─────────────────────────
  // Called by the confirmation card UI when the user clicks Confirm or
  // Cancel on a proposed write action. Fires a request to the server's
  // /messages/confirm endpoint, which resolves the pending promise the
  // streaming loop is awaiting — that unblocks the loop and execution
  // (or the cancelled-by-user tool_result) proceeds.
  //
  // We don't update local state on success: the next SSE event from the
  // open stream will deliver the tool_result and update the badge.
  const confirmAction = async (toolUseId, decision, editedInput) => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/assistant/messages/confirm`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tool_use_id: toolUseId,
          decision, // 'confirmed' | 'cancelled'
          input: editedInput || undefined,
        }),
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        setError(errBody.error || `Confirmation failed (${r.status}).`);
      }
    } catch (e) {
      setError(e?.message || 'Could not send confirmation.');
    }
  };
  const onInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const text = draft.trim();
      if (!text) return;
      if (open && activeConvId) {
        sendMessage();
      } else {
        // Open panel and send once it's ready. The chat-lifecycle
        // effect picks up pendingSend.
        setPendingSend(true);
        setOpen(true);
      }
    }
  };
  // Clicking anywhere on the bar opens the panel (no-op if already open).
  const onBarClick = () => {
    if (!open) setOpen(true);
    inputRef.current?.focus();
  };

  // ── resize ─────────────────────────────────────────────────────────────
  // mode: 'top' | 'right' | 'corner'
  const onResizeStart = (mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = width;
    const startH = height;
    const maxW = window.innerWidth - 32;
    const maxH = window.innerHeight - 80; // leave room for the bar
    const onMove = (ev) => {
      if (mode === 'right' || mode === 'corner') {
        setWidth(clamp(startW + (ev.clientX - startX), MIN_WIDTH, maxW));
      }
      if (mode === 'top' || mode === 'corner') {
        // Top edge: dragging UP (smaller clientY) increases height.
        setHeight(clamp(startH - (ev.clientY - startY), MIN_HEIGHT, maxH));
      }
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = mode === 'top' ? 'ns-resize' : mode === 'right' ? 'ew-resize' : 'nesw-resize';
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  if (!visible) return null;

  // ───────────────────────────────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────────────────────────────

  // Card style — shared between bar, messages container, and toolbars.
  // White bg, gray border, rounded-lg. NO shadow (user wanted no
  // backdrop / no shadow vibe).
  const cardClasses = 'bg-white border border-gray-200 rounded-lg';

  // ── Inline chat surface — the messages + overlays, without the floating
  //    chrome. Reused inside the sidebar slot. Forces visible content since
  //    the sidebar owns open/close. ──────────────────────────────────────
  const chatSurface = (
    <div className="relative w-full h-full overflow-hidden bg-white">
      {/* History overlay */}
      {activeOverlay === 'history' && (
        <div className="absolute inset-0 z-20 bg-white flex flex-col">
          <div className="flex-shrink-0 px-3 py-2 border-b border-gray-200 flex items-center gap-2">
            <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">Recent conversations</span>
            <button
              onClick={() => { startNewConversation(); setActiveOverlay(null); }}
              className="flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors text-xs font-medium"
              title="New conversation">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              <span>New</span>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-6 text-center text-xs text-gray-400">No conversations yet.</div>
            ) : conversations.map(c => (
              <div key={c.id} onClick={() => loadConversation(c.id)}
                className={`group flex items-start justify-between px-3 py-2 cursor-pointer hover:bg-amber-50 border-b border-gray-100 ${c.id === activeConvId ? 'bg-amber-50' : ''}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-gray-900 truncate">{c.title || 'New conversation'}</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{new Date(c.updated_at).toLocaleString()}</div>
                </div>
                <button onClick={(e) => archiveConversation(c.id, e)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-opacity" title="Archive">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" /></svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {activeOverlay === 'tools' && <ToolsContent onClose={() => setActiveOverlay(null)} />}
      {activeOverlay === 'tasks' && <TasksContent />}
      {/* Messages scroll area */}
      <div className="absolute inset-0 overflow-y-auto px-3 py-3">
        {messages.length === 0 && !sending && !streamingMsg ? (
          <EmptyState onPick={(text) => { setDraft(text); }} />
        ) : (
          <div className="space-y-2">
            {messages.map((m) => <MessageBubble key={m.id} message={m} onConfirm={confirmAction} />)}
            {streamingMsg && <MessageBubble key="streaming" message={streamingMsg} streaming onConfirm={confirmAction} />}
            {sending && !streamingMsg && <ThinkingIndicator />}
            <div ref={messageEndRef} />
          </div>
        )}
      </div>
    </div>
  );

  // When the sidebar exposes its slot, render the chat there and skip the
  // entire floating shell. Confirmation modals live outside this return and
  // still render normally.
  if (inline) {
    return createPortal(chatSurface, inlineSlot);
  }
  // Chat now lives exclusively in the assistant sidebar. The old floating
  // shell is retired — render nothing when there's no slot (sidebar closed)
  // so the popup never reappears. `showFloating` stays false; the legacy
  // shell below is kept only for reference and never mounts.
  const showFloating = false;
  if (!showFloating) return null;

  return (
    <>
      {/* No backdrop / click-catcher — the assistant must NOT prevent
          the user from interacting with the page underneath (clicking
          candidate rows, opening profiles, etc.) while it's open.
          Close happens via the floating X, Escape, or Cmd/Ctrl+K. */}

      {/* Main floating column — bottom position is dynamic based on the
          current pill height (broadcast from BottomPill). Formula:
          pillBottom (24px) + pillHeight + gap (10px). Keeps the panel
          visually anchored ~10px above the pill no matter how tall the
          pill has grown. */}
      <div
        className="fixed left-1/2 -translate-x-1/2 z-50 flex flex-col gap-1.5 pointer-events-none"
        style={{
          width: `${width}px`,
          maxWidth: 'calc(100vw - 32px)',
          bottom: `${24 + pillHeight + 10}px`,
        }}>

        {/* ─── ACTIVE STATE EXTRAS (above the bar) ────────────────── */}
        {open && (
          <>
            {/* Resize handles + close button container — sits above the
                messages card. Positioned relative so the resize handles
                can absolutely position themselves at the edges. */}
            <div
              className="relative pointer-events-auto"
              style={{ height: `${height}px` }}>

              {/* Messages card — full bounds of the resize area, contains
                  the scrollable content + history overlay. */}
              <div className={`${cardClasses} absolute inset-0 overflow-hidden`}>
                {/* History overlay */}
                {activeOverlay === 'history' && (
                  <div className="absolute inset-0 z-20 bg-white flex flex-col">
                    <div className="flex-shrink-0 px-3 py-2 border-b border-gray-200 flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                        Recent conversations
                      </span>
                      {/* + New button — inline right after the header
                          text. Matches the panel's close-X hover style
                          (gray hover, not emerald) for consistency with
                          other tertiary controls in the header. */}
                      <button
                        onClick={() => {
                          startNewConversation();
                          setActiveOverlay(null);
                        }}
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors text-xs font-medium"
                        title="New conversation"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <span>New</span>
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                      {conversations.length === 0 ? (
                        <div className="p-6 text-center text-xs text-gray-400">No conversations yet.</div>
                      ) : conversations.map(c => (
                        <div
                          key={c.id}
                          onClick={() => loadConversation(c.id)}
                          className={`group flex items-start justify-between px-3 py-2 cursor-pointer hover:bg-amber-50 border-b border-gray-100 ${c.id === activeConvId ? 'bg-amber-50' : ''}`}>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-gray-900 truncate">{c.title || 'New conversation'}</div>
                            <div className="text-[10px] text-gray-500 mt-0.5">{new Date(c.updated_at).toLocaleString()}</div>
                          </div>
                          <button
                            onClick={(e) => archiveConversation(c.id, e)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-opacity"
                            title="Archive">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                    {/* Usage footer — moved here from the panel→bar gap.
                        Surfaces the same metrics (today's spend, monthly
                        total, daily cap) but only when the user actively
                        opens the menu, keeping the active chat surface
                        free of incidental UI. */}
                    {usage && (
                      <div className="flex-shrink-0 border-t border-gray-200 px-3 py-2 bg-gray-50 text-[10px] text-gray-600">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="font-medium uppercase tracking-wide text-gray-500">Usage</span>
                          <span className="text-gray-400">cap ${(usage.daily_cap_cents / 100).toFixed(0)}/day</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Today: <span className="font-semibold text-gray-900">${(usage.today_cents / 100).toFixed(2)}</span></span>
                          <span>Month: <span className="font-semibold text-gray-900">${(usage.month_cents / 100).toFixed(2)}</span></span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Tools overlay — same-shape as history overlay,
                    rendered above the messages. Content managed by
                    ToolsContent which handles its own drill-down state
                    (Quick Create picker, coming-soon stubs). */}
                {activeOverlay === 'tools' && (
                  <ToolsContent onClose={() => setActiveOverlay(null)} />
                )}

                {/* Tasks overlay — stub for now, full TaskInbox
                    integration in a follow-up. */}
                {activeOverlay === 'tasks' && (
                  <TasksContent />
                )}

                {/* Messages scroll area */}
                <div className="absolute inset-0 overflow-y-auto px-3 py-3">
                  {messages.length === 0 && !sending && !streamingMsg ? (
                    <EmptyState onPick={(text) => { setDraft(text); inputRef.current?.focus(); }} />
                  ) : (
                    <div className="space-y-2">
                      {messages.map((m) => <MessageBubble key={m.id} message={m} />)}
                      {streamingMsg && <MessageBubble key="streaming" message={streamingMsg} streaming onConfirm={confirmAction} />}
                      {sending && !streamingMsg && <ThinkingIndicator />}
                      <div ref={messageEndRef} />
                    </div>
                  )}
                </div>
              </div>

              {/* RESIZE HANDLES — overlay the edges of the messages card.
                  Top edge: 8px hover/drag zone, full width.
                  Right edge: 8px hover/drag zone, full height.
                  Top-right corner: 14px square overlapping both, takes
                  priority via z-index. */}
              <div
                className="absolute -top-1 left-2 right-12 h-3 cursor-ns-resize z-10"
                onPointerDown={onResizeStart('top')}
                title="Drag to resize height"
              />
              <div
                className="absolute -right-1 top-12 bottom-2 w-3 cursor-ew-resize z-10"
                onPointerDown={onResizeStart('right')}
                title="Drag to resize width"
              />
              {/* Visible corner grip removed — user requested only
                  height (top edge) and width (right edge) resizing.
                  Corner/diagonal drag is gone.

              {/* Main close button — inside the panel's top-right corner.
                  Transparent by default; gray hover only. Consistent
                  with the other tertiary controls (hamburger toggle, +
                  button in history header). */}
              <button
                onClick={closePanel}
                className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 z-30 transition-colors"
                title="Close (Esc)">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Error banner (when active) — slots between messages + bar. */}
            {error && (
              <div className="pointer-events-auto bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700 flex items-center justify-between">
                <span className="truncate">{error}</span>
                <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 ml-2 flex-shrink-0">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            {/* Usage indicator used to live here between messages and the
                bar. Moved into the history overlay so the active chat
                surface stays clean. */}
          </>
        )}

        {/* ─── THE BAR — permanently hidden. The BottomPill in the shell
            is now the only input surface; the launcher receives text
            via the banyak:assistant_open_with_text event. Keeping the
            JSX gated by `false` retains all the send/history handlers
            for potential resurrection but avoids rendering the bar. */}
        {false && (
        <div
          className={`${cardClasses} flex-shrink-0 pointer-events-auto flex items-center gap-2 px-2 py-2 cursor-text hover:border-emerald-400 transition-colors`}
          onClick={(e) => {
            // Don't open if user clicked one of the inline buttons —
            // they handle their own click semantics.
            if (e.target.closest('button')) return;
            onBarClick();
          }}>
          {/* History toggle — hamburger when closed, X when open.
              Clicking it again closes the history view, so users have
              a single, obvious button for the menu. */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!open) setOpen(true);
              setActiveOverlay(cur => cur === 'history' ? null : 'history');
            }}
            className={`flex-shrink-0 p-1.5 rounded hover:bg-emerald-50 ${activeOverlay === 'history' ? 'bg-emerald-50 text-emerald-700' : 'text-gray-500 hover:text-emerald-700'}`}
            title={activeOverlay === 'history' ? 'Close menu' : 'Conversation history'}>
            {activeOverlay === 'history' ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>

          {/* Input */}
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder={sending ? 'Waiting for reply…' : 'Ask the assistant…'}
            disabled={sending}
            className="flex-1 min-w-0 bg-transparent text-sm text-gray-800 placeholder:text-gray-400 outline-none border-none"
          />

          {/* New chat (+) */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!open) setOpen(true);
              startNewConversation();
              inputRef.current?.focus();
            }}
            className="flex-shrink-0 p-1.5 rounded text-gray-500 hover:bg-emerald-50 hover:text-emerald-700"
            title="New conversation">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>

          {/* Send (↑) — primary, emerald */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!draft.trim()) return;
              if (open && activeConvId) {
                sendMessage();
              } else {
                setPendingSend(true);
                setOpen(true);
              }
            }}
            disabled={!draft.trim() || sending}
            className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded bg-emerald-700 text-white hover:bg-emerald-800 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
            title="Send (Enter)">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5M5 12l7-7 7 7" />
            </svg>
          </button>
        </div>
        )}
      </div>
    </>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// Sub-components
// ───────────────────────────────────────────────────────────────────────────

function MessageBubble({ message, streaming = false, onConfirm }) {
  const { role, content } = message;
  const blocks = Array.isArray(content) ? content
    : (typeof content === 'string' ? [{ type: 'text', text: content }] : []);

  if (role === 'user') {
    const text = blocks.map(b => b?.text || '').filter(Boolean).join('\n');
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] px-3 py-2 bg-amber-600 text-white text-sm rounded-lg whitespace-pre-wrap break-words border border-amber-700">
          {text}
        </div>
      </div>
    );
  }

  if (role === 'tool') return null;

  const lastBlock = blocks[blocks.length - 1];
  return (
    <div className="flex justify-start">
      <div className="max-w-[95%] space-y-1.5">
        {blocks.map((b, i) => {
          if (b.type === 'tool_use') {
            // Special case 1: render_chart blocks render as a chart
            // panel from the tool input, not as a status badge. The
            // chart spec lives in b.input so this works for both
            // streaming AND replay of past messages.
            if (b.name === 'render_chart') {
              return (
                <ChartBlock
                  key={i}
                  spec={b.input}
                  errored={b.status === 'error'}
                />
              );
            }
            // Special case 2: awaiting_confirmation renders a full
            // confirmation card with the proposed action preview and
            // Confirm/Cancel buttons. Everything else renders the
            // compact status badge.
            if (b.status === 'awaiting_confirmation') {
              return (
                <ConfirmationCard
                  key={i}
                  toolUseId={b.id}
                  toolName={b.name}
                  input={b.input}
                  preview={b.preview}
                  onConfirm={onConfirm}
                />
              );
            }
            return <ToolBadge key={i} block={b} />;
          }
          if (b.type === 'text' && b.text) {
            const isLastBlock = b === lastBlock;
            return (
              <div key={i} className="px-3 py-2 bg-white border border-gray-200 text-gray-800 text-sm rounded-lg prose prose-sm max-w-none assistant-markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_RENDERERS}>
                  {b.text}
                </ReactMarkdown>
                {streaming && isLastBlock && (
                  <span className="inline-block w-1.5 h-3 bg-amber-600 align-middle ml-0.5 animate-pulse" aria-hidden />
                )}
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// ToolBadge — small inline status pill for tool calls.
// ───────────────────────────────────────────────────────────────────────────
// Visualizes the lifecycle of a tool call as a single line:
//   pending/executing → amber pulsing icon
//   complete          → emerald checkmark
//   error             → red warning
//   cancelled         → gray "x" (user declined a confirmation)
//   timeout           → gray clock
// Legacy data (status undefined) is treated as complete.
function ToolBadge({ block }) {
  const label = TOOL_LABELS[block.name] || block.name;
  const status = block.status;
  const isActive = status === 'pending' || status === 'executing';
  const isError = status === 'error';
  const isCancelled = status === 'cancelled';
  const isTimeout = status === 'timeout';

  const palette = isError
    ? 'bg-red-50 border-red-200 text-red-800'
    : isCancelled || isTimeout
      ? 'bg-gray-50 border-gray-200 text-gray-600'
      : isActive
        ? 'bg-amber-50 border-amber-200 text-amber-800'
        : 'bg-emerald-50 border-emerald-200 text-emerald-800';

  let icon;
  if (isActive) {
    icon = (
      <svg className="w-3 h-3 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    );
  } else if (isError) {
    icon = (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  } else if (isCancelled) {
    icon = (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    );
  } else if (isTimeout) {
    icon = (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  } else {
    icon = (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    );
  }

  const suffix = isCancelled ? ' — cancelled'
    : isTimeout ? ' — timed out'
    : '';

  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-1 border rounded-lg text-[11px] ${palette}`}>
      {icon}{label}{suffix}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// ChartBlock — renders an inline chart from a render_chart tool input.
// ───────────────────────────────────────────────────────────────────────────
// Persistence note: the chart's spec is the tool_use block's input, so
// this same component is used both during streaming AND when replaying
// a past conversation. Don't put any per-render side effects in here.

const CHART_PALETTE = [
  '#047857', // emerald-700 (brand)
  '#0f766e', // teal-700
  '#0369a1', // sky-700
  '#7c3aed', // violet-600
  '#c2410c', // orange-700
  '#a16207', // yellow-700
];

function ChartBlock({ spec, errored }) {
  // Defensive: a malformed spec (e.g. from an old persisted message
  // before validation tightened) should fail soft, not crash the
  // whole message render.
  if (!spec || typeof spec !== 'object') {
    return (
      <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-[11px] text-gray-500 italic">
        Chart unavailable (no spec).
      </div>
    );
  }
  if (errored) {
    return (
      <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-[11px] text-red-700">
        Chart failed to render. {spec.title ? `(${spec.title})` : ''}
      </div>
    );
  }

  const { type, title, data, x_label, y_label, data_as_of } = spec;
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-[11px] text-gray-500 italic">
        {title || 'Chart'} — no data.
      </div>
    );
  }

  // Compact relative timestamp for the caption.
  const formattedAsOf = data_as_of ? formatAsOf(data_as_of) : null;

  // Height: 200 default, slightly taller for funnel/line readability.
  const height = type === 'line' ? 220 : type === 'funnel' ? 240 : 200;

  return (
    <div className="px-3 py-2.5 bg-white border border-gray-200 rounded-lg">
      {title && (
        <div className="text-xs font-semibold text-gray-800 mb-2">{title}</div>
      )}
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">
          {renderChartByType(type, data, x_label, y_label)}
        </ResponsiveContainer>
      </div>
      {formattedAsOf && (
        <div className="text-[10px] text-gray-400 mt-1.5 text-right">
          As of {formattedAsOf}
        </div>
      )}
    </div>
  );
}

function renderChartByType(type, data, x_label, y_label) {
  if (type === 'bar') {
    return (
      <BarChart data={data} margin={{ top: 5, right: 12, left: 0, bottom: 24 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-15} textAnchor="end" height={50}
          label={x_label ? { value: x_label, position: 'insideBottom', offset: -5, fontSize: 10, fill: '#6b7280' } : undefined} />
        <YAxis tick={{ fontSize: 10 }}
          label={y_label ? { value: y_label, angle: -90, position: 'insideLeft', fontSize: 10, fill: '#6b7280' } : undefined} />
        <Tooltip contentStyle={{ fontSize: 11, padding: '4px 8px' }} />
        <Bar dataKey="value" fill={CHART_PALETTE[0]} radius={[4, 4, 0, 0]} />
      </BarChart>
    );
  }
  if (type === 'pie') {
    return (
      <PieChart>
        <Tooltip contentStyle={{ fontSize: 11, padding: '4px 8px' }} />
        <Legend wrapperStyle={{ fontSize: 10 }} />
        <Pie data={data} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={70} label={{ fontSize: 10 }}>
          {data.map((_, i) => (
            <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
          ))}
        </Pie>
      </PieChart>
    );
  }
  if (type === 'line') {
    // Multi-series: group by `series` field; single-series otherwise.
    const hasSeries = data.some(d => d.series);
    if (hasSeries) {
      const seriesMap = {};
      const xs = [];
      const xSet = new Set();
      for (const d of data) {
        if (!xSet.has(d.x)) { xs.push(d.x); xSet.add(d.x); }
        if (!seriesMap[d.series]) seriesMap[d.series] = {};
        seriesMap[d.series][d.x] = d.y;
      }
      const wide = xs.map(x => {
        const row = { x };
        for (const s of Object.keys(seriesMap)) row[s] = seriesMap[s][x] ?? null;
        return row;
      });
      const seriesNames = Object.keys(seriesMap);
      return (
        <LineChart data={wide} margin={{ top: 5, right: 12, left: 0, bottom: 24 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="x" tick={{ fontSize: 10 }}
            label={x_label ? { value: x_label, position: 'insideBottom', offset: -5, fontSize: 10, fill: '#6b7280' } : undefined} />
          <YAxis tick={{ fontSize: 10 }}
            label={y_label ? { value: y_label, angle: -90, position: 'insideLeft', fontSize: 10, fill: '#6b7280' } : undefined} />
          <Tooltip contentStyle={{ fontSize: 11, padding: '4px 8px' }} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {seriesNames.map((s, i) => (
            <Line key={s} dataKey={s} stroke={CHART_PALETTE[i % CHART_PALETTE.length]} strokeWidth={2} dot={{ r: 3 }} />
          ))}
        </LineChart>
      );
    }
    return (
      <LineChart data={data} margin={{ top: 5, right: 12, left: 0, bottom: 24 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="x" tick={{ fontSize: 10 }}
          label={x_label ? { value: x_label, position: 'insideBottom', offset: -5, fontSize: 10, fill: '#6b7280' } : undefined} />
        <YAxis tick={{ fontSize: 10 }}
          label={y_label ? { value: y_label, angle: -90, position: 'insideLeft', fontSize: 10, fill: '#6b7280' } : undefined} />
        <Tooltip contentStyle={{ fontSize: 11, padding: '4px 8px' }} />
        <Line dataKey="y" stroke={CHART_PALETTE[0]} strokeWidth={2} dot={{ r: 3 }} />
      </LineChart>
    );
  }
  if (type === 'funnel') {
    // Recharts FunnelChart expects {value, name, fill}. Map for safety
    // and assign palette colors progressively darker as the funnel
    // narrows so the visual "drop-off" is reinforced by color.
    const funnelData = data.map((d, i) => ({
      value: d.value,
      name: d.label,
      fill: CHART_PALETTE[i % CHART_PALETTE.length],
    }));
    return (
      <FunnelChart>
        <Tooltip contentStyle={{ fontSize: 11, padding: '4px 8px' }} />
        <Funnel dataKey="value" data={funnelData} isAnimationActive>
          <LabelList position="right" fill="#374151" stroke="none" fontSize={11}
            dataKey={(entry) => `${entry.name}: ${entry.value}`} />
        </Funnel>
      </FunnelChart>
    );
  }
  return null;
}

// Format an ISO timestamp into a compact relative-or-date caption.
// "5 minutes ago" / "yesterday 3:42 PM" / "Jun 15, 2024".
function formatAsOf(iso) {
  try {
    const t = new Date(iso).getTime();
    if (isNaN(t)) return null;
    const diff = Date.now() - t;
    const mins = Math.round(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return null; }
}

// ───────────────────────────────────────────────────────────────────────────
// ConfirmationCard — gates a proposed write tool behind explicit consent.
// ───────────────────────────────────────────────────────────────────────────
// Renders when the server emits a `confirmation_required` event. Shows
// a tool-specific preview (candidate name + what will change), lets the
// user edit certain fields where appropriate, and submits the decision
// (confirmed | cancelled) via the onConfirm callback.
//
// While submitting, both buttons are disabled to prevent double-clicks.
// On success, the parent's SSE stream delivers a tool_result event
// that reroutes this block into a regular ToolBadge.
//
// Per-tool preview body is dispatched via a switch on toolName. Each
// preview body owns its own editable state (note content textarea, etc)
// and returns it via the onChange callback so the card can ship the
// final edited input to the server when Confirm fires.
function ConfirmationCard({ toolUseId, toolName, input, preview, onConfirm }) {
  // Each tool may want to override the input before sending (edits in
  // the card). Default: the input as Claude proposed it.
  const [editedInput, setEditedInput] = useState(input || {});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // 'confirmed' | 'cancelled' | null

  // Bulk actions: track which candidate rows are enabled (toggleable in
  // the card). Seeded from the preview's items list.
  const isBulk = !!preview?.bulk;
  const [enabledIds, setEnabledIds] = useState(() =>
    isBulk ? new Set((preview.items || []).filter(i => i.enabled !== false).map(i => i.candidate_id)) : null
  );
  useEffect(() => {
    if (isBulk) setEnabledIds(new Set((preview.items || []).filter(i => i.enabled !== false).map(i => i.candidate_id)));
  }, [isBulk, preview]);

  // If Claude re-proposes (input prop changes), reset local edits to match.
  useEffect(() => { setEditedInput(input || {}); }, [input]);

  const handleClick = async (decision) => {
    if (submitting || submitted) return;
    setSubmitting(true);
    setSubmitted(decision);
    // For bulk, narrow candidate_ids to only the enabled rows.
    let finalInput = editedInput;
    if (decision === 'confirmed' && isBulk) {
      finalInput = { ...editedInput, candidate_ids: Array.from(enabledIds) };
    }
    finalInput = decision === 'confirmed' ? finalInput : null;
    try {
      await onConfirm(toolUseId, decision, finalInput);
    } finally {
      setSubmitting(false);
    }
  };

  // Some previews can detect their target is invalid (e.g. unknown
  // stage name) and want to block confirm even if the user clicks. We
  // expose this via the preview.target_stage_found flag for move, and
  // for add_note via editedContent emptiness. Per-tool logic below.
  const isConfirmDisabled = (() => {
    if (submitting || submitted) return true;
    if (isBulk) return !enabledIds || enabledIds.size === 0;
    if (toolName === 'add_note') {
      const c = editedInput?.content;
      return !c || !String(c).trim();
    }
    if (toolName === 'move_candidate_stage') {
      // Block if we couldn't resolve the target stage in preview.
      return preview && preview.target_stage_found === false;
    }
    if (toolName === 'add_candidate_to_pipeline') {
      // Block if the preview surfaced a warning (missing pipeline,
      // no stages, no access, etc).
      return !!(preview && preview.warning);
    }
    if (toolName === 'update_candidate_rating') {
      const r = Number(editedInput?.rating);
      return !Number.isFinite(r) || r < 1 || r > 5;
    }
    return false;
  })();

  return (
    <div className="w-full bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2.5">
      <div className="flex items-center gap-2">
        <svg className="w-4 h-4 text-amber-700 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span className="text-xs font-semibold text-amber-900 uppercase tracking-wide">
          {preview?.title || 'Confirm action'}
        </span>
      </div>

      {preview?.warning && (
        <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          ⚠ {preview.warning}
        </div>
      )}

      {isBulk ? (
        <div className="space-y-2">
          {/* Exact value being applied — so the user knows precisely what
              this does before confirming. */}
          <div className="rounded-md bg-white border border-amber-200 px-2.5 py-2">
            <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold mb-0.5">{preview.action_label}</div>
            {preview.detail_kind === 'note' && (
              <div className="text-xs text-gray-800 whitespace-pre-wrap break-words">"{preview.detail}"</div>
            )}
            {preview.detail_kind === 'tag' && (
              <span className="inline-block text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">{preview.detail}</span>
            )}
            {preview.detail_kind === 'rating' && (
              <div className="text-xs text-gray-800">{preview.detail} / 5 stars</div>
            )}
          </div>

          <div className="text-[10px] text-gray-500">Applying to these candidates:</div>
          <div className="max-h-48 overflow-y-auto rounded border border-amber-200 bg-white divide-y divide-gray-100">
            {(preview.items || []).map(item => {
              const on = enabledIds?.has(item.candidate_id);
              return (
                <button
                  key={item.candidate_id}
                  type="button"
                  disabled={submitting || !!submitted}
                  onClick={() => setEnabledIds(prev => {
                    const next = new Set(prev);
                    if (next.has(item.candidate_id)) next.delete(item.candidate_id); else next.add(item.candidate_id);
                    return next;
                  })}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-amber-50 disabled:cursor-not-allowed"
                >
                  <span className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border ${on ? 'bg-emerald-600 border-emerald-600' : 'bg-white border-gray-300'}`}>
                    {on && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                  </span>
                  <span className={`text-xs truncate ${on ? 'text-gray-900' : 'text-gray-400 line-through'}`}>{item.name}</span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between text-[10px] text-gray-500">
            <span>{enabledIds?.size || 0} of {preview.items?.length || 0} selected</span>
            <div className="flex gap-2">
              <button type="button" disabled={submitting || !!submitted} onClick={() => setEnabledIds(new Set((preview.items || []).map(i => i.candidate_id)))} className="text-amber-700 hover:underline">All</button>
              <button type="button" disabled={submitting || !!submitted} onClick={() => setEnabledIds(new Set())} className="text-amber-700 hover:underline">None</button>
            </div>
          </div>
        </div>
      ) : (
        <ConfirmationBody
          toolName={toolName}
          input={editedInput}
          preview={preview}
          onChange={setEditedInput}
          disabled={submitting || !!submitted}
        />
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          onClick={() => handleClick('cancelled')}
          disabled={submitting || !!submitted}
          className="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
          {submitted === 'cancelled' ? 'Cancelled' : 'Cancel'}
        </button>
        <button
          onClick={() => handleClick('confirmed')}
          disabled={isConfirmDisabled}
          className="px-3 py-1.5 text-xs text-white bg-emerald-700 border border-emerald-800 rounded hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed">
          {submitted === 'confirmed'
            ? 'Confirmed'
            : isBulk
              ? `Apply ${enabledIds?.size || 0} change${(enabledIds?.size || 0) === 1 ? '' : 's'}`
              : 'Confirm'}
        </button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// ConfirmationBody — per-tool body rendering for the confirmation card.
// ───────────────────────────────────────────────────────────────────────────
// New write tools add a case here. Receives the current editedInput +
// the server-side preview payload + an onChange hook to update the
// editedInput as the user types.
function ConfirmationBody({ toolName, input, preview, onChange, disabled }) {
  if (toolName === 'add_note') return <AddNoteBody input={input} preview={preview} onChange={onChange} disabled={disabled} />;
  if (toolName === 'tag_candidate') return <TagCandidateBody input={input} preview={preview} onChange={onChange} disabled={disabled} />;
  if (toolName === 'remove_tag') return <RemoveTagBody input={input} preview={preview} />;
  if (toolName === 'move_candidate_stage') return <MoveCandidateStageBody input={input} preview={preview} />;
  if (toolName === 'add_candidate_to_pipeline') return <AddCandidateToPipelineBody input={input} preview={preview} />;
  if (toolName === 'update_candidate_rating') return <UpdateCandidateRatingBody input={input} preview={preview} onChange={onChange} disabled={disabled} />;
  if (toolName === 'update_search') return <UpdateSearchBody input={input} preview={preview} onChange={onChange} disabled={disabled} />;

  // Fallback for unknown tools — show raw input so the user can at
  // least see what's being proposed before deciding.
  return (
    <pre className="text-[11px] text-gray-700 bg-white border border-gray-200 rounded p-2 overflow-x-auto">
      {JSON.stringify(input, null, 2)}
    </pre>
  );
}

function UpdateSearchBody({ input, preview, onChange, disabled }) {
  // Manual override: the user can edit each field in the card before
  // confirming, mirroring the candidate write cards.
  const set = (k, v) => onChange({ ...input, [k]: v });
  const has = (k) => input && input[k] !== undefined;

  return (
    <div className="space-y-2">
      {preview?.search_name && (
        <div className="text-xs text-gray-800">
          <span className="text-gray-500">Search: </span>
          <span className="font-medium">{preview.search_name}</span>
        </div>
      )}

      {has('status') && (
        <div>
          <label className="text-[10px] text-gray-500 uppercase tracking-wide">Status</label>
          <select
            value={input.status ?? 'active'}
            onChange={(e) => set('status', e.target.value)}
            disabled={disabled}
            className="mt-0.5 w-full text-xs px-2 py-1.5 bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-50"
          >
            <option value="active">Active</option>
            <option value="on_hold">On Hold</option>
            <option value="closed">Closed</option>
            <option value="placement">Placement</option>
            <option value="cancelled">Cancelled</option>
            <option value="internal_hire">Internal Hire</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      )}

      {has('location') && (
        <div>
          <label className="text-[10px] text-gray-500 uppercase tracking-wide">Location</label>
          <input
            value={input.location ?? ''}
            onChange={(e) => set('location', e.target.value)}
            disabled={disabled}
            className="mt-0.5 w-full text-xs px-2 py-1.5 bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-50"
          />
        </div>
      )}

      {has('name') && (
        <div>
          <label className="text-[10px] text-gray-500 uppercase tracking-wide">Name</label>
          <input
            value={input.name ?? ''}
            onChange={(e) => set('name', e.target.value)}
            disabled={disabled}
            className="mt-0.5 w-full text-xs px-2 py-1.5 bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-50"
          />
        </div>
      )}

      {Array.isArray(preview?.changes) && preview.changes.length > 0 && (
        <div className="text-[10px] text-gray-500 space-y-0.5">
          {preview.changes.map((c, i) => (
            <div key={i}><span className="text-gray-400">{c.field}:</span> {c.from} → <span className="text-gray-700">{c.to}</span></div>
          ))}
        </div>
      )}
      {preview?.note && <div className="text-[10px] text-gray-500 italic">{preview.note}</div>}
    </div>
  );
}

function AddNoteBody({ input, preview, onChange, disabled }) {
  // The note content is editable. Anything else (candidate_id) is
  // displayed but not editable.
  const setContent = (content) => onChange({ ...input, content });
  return (
    <div className="space-y-1.5">
      {preview && (
        <div className="text-xs text-gray-800">
          <span className="text-gray-500">For: </span>
          <span className="font-medium">{preview.candidate_name}</span>
          {preview.candidate_context && (
            <span className="text-gray-500"> · {preview.candidate_context}</span>
          )}
        </div>
      )}
      <div>
        <label className="text-[10px] text-gray-500 uppercase tracking-wide">Note content</label>
        <textarea
          value={input?.content ?? ''}
          onChange={(e) => setContent(e.target.value)}
          disabled={disabled}
          rows={3}
          className="mt-0.5 w-full text-xs px-2 py-1.5 bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-50"
        />
      </div>
      {preview?.note && (
        <div className="text-[10px] text-gray-500 italic">{preview.note}</div>
      )}
    </div>
  );
}

function TagCandidateBody({ input, preview, onChange, disabled }) {
  // Tag name is editable so the user can correct typos or rename
  // before confirming. Candidate target is fixed.
  const setTag = (tag) => onChange({ ...input, tag });
  return (
    <div className="space-y-1.5">
      {preview && (
        <div className="text-xs text-gray-800">
          <span className="text-gray-500">For: </span>
          <span className="font-medium">{preview.candidate_name}</span>
          {preview.candidate_context && (
            <span className="text-gray-500"> · {preview.candidate_context}</span>
          )}
        </div>
      )}
      <div>
        <label className="text-[10px] text-gray-500 uppercase tracking-wide">Tag</label>
        <input
          type="text"
          value={input?.tag ?? ''}
          onChange={(e) => setTag(e.target.value)}
          disabled={disabled}
          className="mt-0.5 w-full text-xs px-2 py-1.5 bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-50"
        />
      </div>
      {preview?.note && (
        <div className="text-[10px] text-gray-500 italic">{preview.note}</div>
      )}
    </div>
  );
}

function MoveCandidateStageBody({ input, preview }) {
  // Stage moves are NOT editable in the card — stages are scoped to a
  // pipeline and the validation is delicate (case-insensitive name
  // lookup, must exist on the same pipeline). If the user wants to
  // change the destination, they Cancel and ask Claude to retry with
  // a different stage. The preview shows the available stages so
  // they know what to ask for.
  if (!preview) return null;
  return (
    <div className="space-y-2">
      <div className="text-xs text-gray-800">
        <span className="text-gray-500">Candidate: </span>
        <span className="font-medium">{preview.candidate_name}</span>
        {preview.candidate_context && (
          <span className="text-gray-500"> · {preview.candidate_context}</span>
        )}
      </div>
      <div className="text-xs text-gray-800">
        <span className="text-gray-500">Pipeline: </span>
        <span className="font-medium">{preview.pipeline_name}</span>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <div className="flex-1 px-2 py-1.5 bg-gray-100 border border-gray-200 rounded text-xs text-gray-700">
          <div className="text-[9px] uppercase tracking-wide text-gray-500">From</div>
          <div className="font-medium truncate">{preview.from_stage}</div>
        </div>
        <svg className="w-4 h-4 text-amber-700 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
        </svg>
        <div className={`flex-1 px-2 py-1.5 border rounded text-xs ${preview.target_stage_found ? 'bg-emerald-50 border-emerald-300 text-emerald-900' : 'bg-red-50 border-red-300 text-red-900'}`}>
          <div className="text-[9px] uppercase tracking-wide">To</div>
          <div className="font-medium truncate">{preview.to_stage}</div>
        </div>
      </div>
      {!preview.target_stage_found && (
        <div className="text-[10px] text-gray-500 space-y-0.5">
          {preview.available_internal_stages?.length > 0 && (
            <div><span className="font-semibold text-gray-600">Internal stages:</span> <span className="text-gray-700">{preview.available_internal_stages.join(', ')}</span></div>
          )}
          {preview.available_client_stages?.length > 0 && (
            <div><span className="font-semibold text-gray-600">Client stages:</span> <span className="text-gray-700">{preview.available_client_stages.join(', ')}</span></div>
          )}
        </div>
      )}
      {preview.target_stage_found && preview.stage_kind && (
        <div className="text-[10px] text-gray-500 italic">
          Moving to a <span className="font-medium">{preview.stage_kind}</span> stage.
        </div>
      )}
    </div>
  );
}

function RemoveTagBody({ input, preview }) {
  // Tag and candidate are both fixed here — confirm or cancel.
  if (!preview) return null;
  return (
    <div className="space-y-1.5">
      <div className="text-xs text-gray-800">
        <span className="text-gray-500">From: </span>
        <span className="font-medium">{preview.candidate_name}</span>
        {preview.candidate_context && (
          <span className="text-gray-500"> · {preview.candidate_context}</span>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-gray-500 uppercase tracking-wide">Tag:</span>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-700 text-xs font-medium line-through decoration-2">
          {preview.tag}
        </span>
      </div>
      {preview.note && <div className="text-[10px] text-gray-500 italic">{preview.note}</div>}
    </div>
  );
}

function AddCandidateToPipelineBody({ input, preview }) {
  if (!preview) return null;
  return (
    <div className="space-y-1.5">
      <div className="text-xs text-gray-800">
        <span className="text-gray-500">Candidate: </span>
        <span className="font-medium">{preview.candidate_name}</span>
        {preview.candidate_context && (
          <span className="text-gray-500"> · {preview.candidate_context}</span>
        )}
      </div>
      <div className="text-xs text-gray-800">
        <span className="text-gray-500">Pipeline: </span>
        <span className="font-medium">{preview.pipeline_name}</span>
      </div>
      {preview.first_stage_name && !preview.already_on_pipeline && (
        <div className="text-xs text-gray-800">
          <span className="text-gray-500">Landing stage: </span>
          <span className="font-medium text-emerald-700">{preview.first_stage_name}</span>
          <span className="text-gray-500"> · priority medium</span>
        </div>
      )}
      {preview.note && <div className="text-[10px] text-gray-500 italic">{preview.note}</div>}
    </div>
  );
}

function UpdateCandidateRatingBody({ input, preview, onChange, disabled }) {
  // Rating + feedback are both editable. The star picker shows current
  // vs proposed visually so the user can spot a typo (claude meant 4,
  // typed 5).
  const setRating = (rating) => onChange({ ...input, rating });
  const setFeedback = (feedback) => onChange({ ...input, feedback });
  const proposed = Math.max(1, Math.min(5, Math.round(Number(input?.rating) || 0)));
  return (
    <div className="space-y-2">
      {preview && (
        <>
          <div className="text-xs text-gray-800">
            <span className="text-gray-500">Candidate: </span>
            <span className="font-medium">{preview.candidate_name}</span>
            {preview.candidate_context && (
              <span className="text-gray-500"> · {preview.candidate_context}</span>
            )}
          </div>
          <div className="text-xs text-gray-800">
            <span className="text-gray-500">Pipeline: </span>
            <span className="font-medium">{preview.pipeline_name}</span>
          </div>
        </>
      )}
      <div>
        <label className="text-[10px] text-gray-500 uppercase tracking-wide">Rating</label>
        <div className="mt-0.5 flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n)}
              disabled={disabled}
              className={`w-7 h-7 flex items-center justify-center rounded text-sm transition ${
                n <= proposed
                  ? 'bg-amber-100 text-amber-700 border border-amber-300'
                  : 'bg-white text-gray-300 border border-gray-200 hover:bg-gray-50'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              aria-label={`Set rating to ${n}`}
            >
              ★
            </button>
          ))}
          <span className="ml-2 text-xs text-gray-600">{proposed}/5</span>
          {preview?.current_rating != null && preview.current_rating !== proposed && (
            <span className="ml-1 text-[10px] text-gray-400">(was {preview.current_rating}/5)</span>
          )}
        </div>
      </div>
      <div>
        <label className="text-[10px] text-gray-500 uppercase tracking-wide">Feedback (optional)</label>
        <textarea
          value={input?.feedback ?? ''}
          onChange={(e) => setFeedback(e.target.value)}
          disabled={disabled}
          rows={2}
          placeholder="Free-text comment stored alongside the rating"
          className="mt-0.5 w-full text-xs px-2 py-1.5 bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-50"
        />
      </div>
      {preview?.note && <div className="text-[10px] text-gray-500 italic">{preview.note}</div>}
    </div>
  );
}

const MARKDOWN_RENDERERS = {
  table: ({ node, ...props }) => (
    <div className="overflow-x-auto my-2 -mx-1">
      <table className="text-xs border-collapse w-full" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => <thead className="bg-emerald-50" {...props} />,
  th: ({ node, ...props }) => <th className="px-2 py-1 text-left font-medium text-emerald-900 border border-emerald-200" {...props} />,
  td: ({ node, ...props }) => <td className="px-2 py-1 border border-gray-300 align-top" {...props} />,
  code: ({ inline, ...props }) => inline
    ? <code className="bg-gray-100 text-gray-900 px-1 py-0.5 rounded text-[11px] font-mono" {...props} />
    : <code className="block bg-gray-900 text-gray-100 p-2 rounded text-[11px] font-mono overflow-x-auto" {...props} />,
  p: ({ node, ...props }) => <p className="my-1.5 leading-relaxed" {...props} />,
  ul: ({ node, ...props }) => <ul className="my-1.5 pl-5 list-disc space-y-0.5" {...props} />,
  ol: ({ node, ...props }) => <ol className="my-1.5 pl-5 list-decimal space-y-0.5" {...props} />,
  h1: ({ node, ...props }) => <h1 className="text-base font-bold mt-2 mb-1" {...props} />,
  h2: ({ node, ...props }) => <h2 className="text-sm font-bold mt-2 mb-1" {...props} />,
  h3: ({ node, ...props }) => <h3 className="text-sm font-semibold mt-1.5 mb-0.5" {...props} />,
  a: ({ node, ...props }) => <a className="text-emerald-700 hover:underline" target="_blank" rel="noopener noreferrer" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
};

function ThinkingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg flex items-center gap-1.5">
        <span className="text-xs text-gray-500">Thinking</span>
        <span className="flex gap-0.5">
          {[0, 150, 300].map((d, i) => (
            <span
              key={i}
              className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse"
              style={{ animationDelay: `${d}ms`, animationDuration: '1.2s' }}
            />
          ))}
        </span>
      </div>
    </div>
  );
}

function EmptyState({ onPick }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center py-6">
      <img src="/Logo-Symbol-Colored.svg" alt="" className="w-10 h-10 mb-3 opacity-80" />
      <h3 className="text-sm font-semibold text-gray-900 mb-1">Banyak Assistant</h3>
      <p className="text-xs text-gray-500 mb-5 max-w-xs px-4">
        Ask me about your candidates, searches, and pipelines. Read-only for now.
      </p>
      <div className="space-y-2 w-full max-w-xs px-4">
        {STARTER_PROMPTS.map((prompt, i) => (
          <button
            key={i}
            onClick={() => onPick(prompt)}
            className="w-full px-3 py-2 text-left text-xs text-gray-700 bg-white hover:bg-emerald-50 hover:text-emerald-800 border border-gray-200 hover:border-emerald-300 rounded-lg transition-colors">
            {prompt}
          </button>
        ))}
      </div>
    </div>
  );
}

export default AssistantLauncher;
